// src/pages/fiscalization/FiscalReceiptsPage.tsx
// Fiscal receipts ledger — status, delivery, retry/abandon flows, and
// corrections. The primary "did this reach the tax authority" surface.
//
// Covers: ListFiscalReceipts, GetFiscalReceipt, CreateFiscalReceipt,
// RetryFiscalReceipt, CreateCorrectionReceipt.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useFiscalizationFiscalReceipt, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { SplitView } from '@/components/SplitView';
import type { FiscalReceipt } from '@core/types/fiscalization';
import { FiscalReceiptStatusValues } from '@core/types/fiscalization';
import { formatDateTime, formatMoney, humanizeEnum, errMessage } from '@/features/fiscalization/format';
import { mapReceiptStatusToVariant } from '@/features/fiscalization/mapStatusToVariant';
import { NewReceiptModal, type NewReceiptValue } from '@/features/fiscalization/NewReceiptModal';
import { CorrectionReceiptModal, type CorrectionReceiptValue } from '@/features/fiscalization/CorrectionReceiptModal';

const STATUS_OPTIONS = FiscalReceiptStatusValues.filter((v) => v !== 'FISCAL_RECEIPT_STATUS_UNSPECIFIED');

const RETRYABLE = new Set(['FISCAL_RECEIPT_STATUS_FAILED', 'FISCAL_RECEIPT_STATUS_PROVIDER_ERROR', 'FISCAL_RECEIPT_STATUS_DELIVERY_FAILED', 'FISCAL_RECEIPT_STATUS_ABANDONED']);

export const FiscalReceiptsPage = observer(() => {
  const store = useFiscalizationFiscalReceipt();
  const { t } = useI18n();
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<FiscalReceipt | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = () => {
    store.ListFiscalReceipts({
      pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any,
      ...(statusFilter ? { status: statusFilter } : {}),
    } as any);
  };

  useEffect(() => { load(); }, [statusFilter]);

  const openDetail = async (r: FiscalReceipt) => {
    setSelected(r);
    if (r.entity?.id) {
      try {
        const full = await store.GetFiscalReceipt(r.entity.id);
        setSelected(full);
      } catch {
        // keep list-row version
      }
    }
  };

  const handleCreate = async (value: NewReceiptValue) => {
    await store.CreateFiscalReceipt({
      entity: {} as any,
      fiscal_point: { id: value.fiscal_point_id } as any,
      type: value.type,
      customer_email: value.customer_email,
      customer_phone: value.customer_phone,
      cashier_name: value.cashier_name,
      cashier_inn: value.cashier_inn,
      items: {
        items: [{
          name: value.item_name,
          quantity: value.item_quantity,
          unit: 'pcs',
          unit_price: { amount: Math.round(Number(value.item_unit_price) * 100), currency: 'CURRENCY_USD', decimal_places: 2 },
          amount: { amount: Math.round(Number(value.item_unit_price) * Number(value.item_quantity || '1') * 100), currency: 'CURRENCY_USD', decimal_places: 2 },
          vat_type: value.item_vat_type,
          payment_method: 'FISCAL_PAYMENT_METHOD_FULL_PAYMENT',
          payment_object: 'FISCAL_PAYMENT_OBJECT_COMMODITY',
          metadata: {},
        }],
      },
      payments: {
        payments: [{
          type: value.payment_type,
          amount: { amount: Math.round(Number(value.item_unit_price) * Number(value.item_quantity || '1') * 100), currency: 'CURRENCY_USD', decimal_places: 2 },
        }],
      },
      taxation_system: 'TAXATION_SYSTEM_UNSPECIFIED',
      submit_immediately: true,
      idempotency_key: crypto.randomUUID(),
      metadata: {},
    } as any);
    setNewOpen(false);
    load();
  };

  const handleRetry = async (r: FiscalReceipt) => {
    if (!r.entity?.id) return;
    setRetryingId(r.entity.id);
    setActionError(null);
    try {
      const res = await store.RetryFiscalReceipt(r.entity.id, {} as any);
      setSelected(res.fiscal_receipt ?? null);
      load();
    } catch (err) {
      setActionError(errMessage(err, t('fiscalization.receipts.retryError')));
    } finally {
      setRetryingId(null);
    }
  };

  const handleCorrection = async (value: CorrectionReceiptValue) => {
    if (!selected?.fiscal_point) return;
    await store.CreateCorrectionReceipt({
      entity: {} as any,
      fiscal_point: selected.fiscal_point,
      correction_type: value.correction_type,
      correction_reason: value.correction_reason,
      customer_email: selected.customer_email,
      customer_phone: selected.customer_phone,
      cashier_name: selected.cashier?.display_name ?? '',
      cashier_inn: '',
      items: selected.items ?? { items: [] },
      payments: selected.payments ?? { payments: [] },
      taxation_system: selected.taxation_system ?? 'TAXATION_SYSTEM_UNSPECIFIED',
      idempotency_key: crypto.randomUUID(),
      metadata: {},
    } as any);
    setCorrectionOpen(false);
    load();
  };

  const list = (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/fiscalization" className="text-muted-foreground hover:text-foreground">{t('fiscalization.dashboard.title')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('fiscalization.receipts.title')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('fiscalization.receipts.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('fiscalization.receipts.subtitle')}</p>
        </div>
        <Button onClick={() => setNewOpen(true)}>{t('fiscalization.receipts.new')}</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} placeholder={t('fiscalization.receipts.allStatuses')} className="sm:w-64" aria-label={t('common.status')}>
            <option value="">{t('fiscalization.receipts.allStatuses')}</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{humanizeEnum(s, 'FISCAL_RECEIPT_STATUS_')}</option>
            ))}
          </Select>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fiscalization.receipts.type')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead className="text-right">{t('fiscalization.receipts.total')}</TableHead>
              <TableHead>{t('fiscalization.common.created')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={5} message={t('fiscalization.receipts.empty')} />
            ) : (
              store.items.map((r) => (
                <TableRow key={r.entity?.id}>
                  <TableCell className="cursor-pointer" onClick={() => openDetail(r)}>{humanizeEnum(r.type, 'FISCAL_RECEIPT_TYPE_')}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => openDetail(r)}><Badge variant={mapReceiptStatusToVariant(r.status)} size="sm">{humanizeEnum(r.status, 'FISCAL_RECEIPT_STATUS_')}</Badge></TableCell>
                  <TableCell className="text-right cursor-pointer" onClick={() => openDetail(r)}>{formatMoney(r.total_amount)}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => openDetail(r)}>{formatDateTime(r.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {r.status && RETRYABLE.has(r.status) && (
                      <Button variant="ghost" size="sm" isLoading={retryingId === r.entity?.id} onClick={() => handleRetry(r)}>{t('fiscalization.common.retry')}</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );

  const detail = selected ? (
    <div className="p-6 space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{humanizeEnum(selected.type, 'FISCAL_RECEIPT_TYPE_')}</h2>
        <p className="text-sm text-muted-foreground">{selected.fiscal_document_number || t('fiscalization.receipts.notFiscalizedYet')}</p>
      </div>

      {actionError && <p className="text-sm text-destructive">{actionError}</p>}

      <Badge variant={mapReceiptStatusToVariant(selected.status)} size="sm">{humanizeEnum(selected.status, 'FISCAL_RECEIPT_STATUS_')}</Badge>

      <dl className="divide-y divide-border text-sm">
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.receipts.total')}</dt><dd className="col-span-2">{formatMoney(selected.total_amount)}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.receipts.customerEmail')}</dt><dd className="col-span-2">{selected.customer_email || '--'}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.common.created')}</dt><dd className="col-span-2">{formatDateTime(selected.created_at)}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.receipts.fiscalized')}</dt><dd className="col-span-2">{formatDateTime(selected.fiscalized_at)}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.receipts.delivered')}</dt><dd className="col-span-2">{formatDateTime(selected.delivered_at)}</dd></div>
        {selected.failure_message && (
          <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.receipts.failure')}</dt><dd className="col-span-2 text-destructive">{selected.failure_message}</dd></div>
        )}
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.receipts.retryCount')}</dt><dd className="col-span-2">{selected.retry_count} / {selected.max_retries}</dd></div>
      </dl>

      <div>
        <h3 className="text-sm font-medium text-foreground mb-2">{t('fiscalization.receipts.lineItems')}</h3>
        {selected.items?.items?.length ? (
          <ul className="space-y-1 text-sm">
            {selected.items.items.map((it) => (
              <li key={it.id} className="flex justify-between">
                <span>{it.name} × {it.quantity}</span>
                <span>{formatMoney(it.amount)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t('fiscalization.receipts.noItems')}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {selected.status && RETRYABLE.has(selected.status) && (
          <Button size="sm" isLoading={retryingId === selected.entity?.id} onClick={() => handleRetry(selected)}>{t('fiscalization.common.retry')}</Button>
        )}
        {selected.status === 'FISCAL_RECEIPT_STATUS_FISCALIZED' || selected.status === 'FISCAL_RECEIPT_STATUS_DELIVERED' ? (
          <Button size="sm" variant="secondary" onClick={() => setCorrectionOpen(true)}>{t('fiscalization.receipts.correction')}</Button>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <>
      <SplitView detail={detail} onClose={() => setSelected(null)}>
        {list}
      </SplitView>

      <NewReceiptModal
        open={newOpen}
        isSaving={store.isSaving}
        onCancel={() => setNewOpen(false)}
        onSubmit={handleCreate}
      />

      <CorrectionReceiptModal
        open={correctionOpen}
        receipt={selected}
        isSaving={store.isSaving}
        onCancel={() => setCorrectionOpen(false)}
        onSubmit={handleCorrection}
      />
    </>
  );
});

export default FiscalReceiptsPage;
